import {
  Button,
  FormHelperText,
  Step,
  StepLabel,
  Stepper,
  Switch
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BranchStatus } from "../../constants/branch-status";
import { useToast } from "../../context/toast-context";
import { BranchOrigin } from "../../enums/branch";
import { BranchService } from "../../services/branch-service";
import { BranchBasicInfo, BranchSettings, ManagerInfo } from "../../services/dto/request/branches-request-dto";
import { PaycloudBranch } from "../../types";
import { refactorLink } from "../../utils/links";
import { isSameAddress } from "../../utils/paycloud";
import { WarningModal } from "../WarningModal";
import { ManagerFields } from "./ManagerFields";
import { ButtonForm } from "./button-form";
import { BranchInfoStep } from "./first-login/add-branch/branch-info-step";
import { validateStepOne, validateStepTwo } from "./first-login/add-branch/validations";
import { LoadingModal } from "../LoadingModal";
import { compressImage } from "../../utils/compress-image";
import { useBranchFiles } from "../../hooks/useBranchFiles";
import { useAuth } from "../../context/auth-context";
import { getCountryCode, COUNTRIES } from "../../constants/countries";
import { buildEditBranchParamsByCountry } from "./first-login/add-branch/build-edit-branch-params-by-country";

function isCloverOnlyBranch(branch: PaycloudBranch): boolean {
  const origins = (branch.origins || []).filter(Boolean);
  return origins.length > 0 && origins.every((origin) => origin === BranchOrigin.CLOVER);
}

interface EditBranchFormProps {
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  branch: PaycloudBranch;
  updateBranch: (branch: PaycloudBranch[]) => void;
  onIncompleteCloverCompleted?: (branch: PaycloudBranch, identityNumber: string) => void;
}

export function EditBranchForm({
  setIsModalOpen,
  branch,
  updateBranch,
  onIncompleteCloverCompleted,
}: EditBranchFormProps) {
  const { t } = useTranslation();
  const { country } = useAuth();
  const countryCode = getCountryCode(country);
  const isDominicanRepublic = countryCode === COUNTRIES.DO;
  // Solo-CLOVER sin cuenta propinas: no usa flujo Paycloud. Con withTipAccount sí completa INCOMPLETE.
  const isCloverWithoutTipAccount = isCloverOnlyBranch(branch) && !branch.withTipAccount;
  const hasCloverOrigin = (branch.origins || []).includes(BranchOrigin.CLOVER);
  const wasIncompleteClover =
    hasCloverOrigin &&
    branch.withTipAccount &&
    branch.status === BranchStatus.INCOMPLETE;
  const requiresTipAccountFlow =
    branch.withTipAccount &&
    branch.status === BranchStatus.INCOMPLETE;
  
  const [branchBasicInfo, setBranchBasicInfo] = useState<BranchBasicInfo>({
    name: branch.name,
    fantasyName: branch.fantasyName || branch.name,
    address: branch.address,
    phone: branch.phone,
    floor: branch.floor ?? "",
    postalCode: branch.postalCode ?? "",
    department: branch.department ?? "",
    branchProvinceId: branch.departmentId ?? 0,
    branchCityId: branch.departmentId > 0 ? branch.cityId : 0, // Manejamos casos de sucursales que se crearon antes de guardar la provincia
    sameAddress: isSameAddress(branch),
    activityAddress: branch.activityAddress ?? "",
    activityFloor: branch.activityFloor ?? "",
    activityDepartment: branch.activityDepartment ?? "",
    activityPostalCode: branch.activityPostalCode ?? "",
    activityProvinceId: branch.activityDepartmentId ?? 0,
    activityCityId: branch.activityDepartmentId > 0 ? branch.activityCityId : 0, // Manejamos casos de sucursales que se crearon antes de guardar la provincia
    afipFile: null, // Para nuevos archivos cargados
    birthDate: "",
    occupation: "",
    ivaCondition: "",
    isPEP: false,
    pepPosition: "",
    isUIF: false,
    gender: "",
    civilStatus: "",
  });

  const [branchSettings, setBranchSettings] = useState<BranchSettings>({
    onlyRatings: branch.onlyRatings,
    googleReviewLink: branch.googleLink ?? "",
    identityType: branch.tributaryIdentifierType as "CUIT" | "CUIL",
    identityNumber: branch.tributaryIdentifier,
    personType: branch.isPhysicalPerson ? "PHYSICAL" : "LEGAL",
    isTaxPayer: branch.taxPayer,
    equalSplitTip: branch.equalSplitTip,
    createCBU: isCloverWithoutTipAccount ? false : branch.withTipAccount,
  });

  const [managerInfo, setManagerInfo] = useState<ManagerInfo>({
    isInCharge: isCloverWithoutTipAccount || branch.status !== BranchStatus.INCOMPLETE,
    managerName: branch.managerName ?? "",
    managerLastName: branch.managerLastName ?? "",
    managerPhone: branch.managerPhone ?? "",
    managerAddress: branch.managerAddress ?? "",
    managerDocumentType: branch.managerDocumentType as "CUIL",
    managerDocumentNumber: branch.managerDocumentNumber ?? "",
    managerProvinceId: branch.managerProvinceId ?? 0,
    managerCityId: branch.managerCityId ?? 0,
    managerDniFront: null, // Para nuevos archivos
    managerDniBack: null, // Para nuevos archivos
    managerSelfie: null, // Para nuevos archivos
  });

  // Hook para manejar archivos existentes con lazy loading
  const { files: branchFiles, isLoading: isLoadingFiles } = useBranchFiles(
    branch.branchId,
    true // Habilitado por defecto para cargar archivos al abrir el modal
  );
  
  // Convertir null a undefined para compatibilidad con las funciones existentes
  const existingFiles = branchFiles ? {
    afipFile: branchFiles.afipFile,
    managerDniFront: branchFiles.managerDniFront,
    managerDniBack: branchFiles.managerDniBack,
    managerSelfie: branchFiles.managerSelfie,
  } : undefined;
  const [activeStep, setActiveStep] = useState(0);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const { showToast } = useToast();
  const branchService = useMemo(() => new BranchService(), []);

  const handleNext = () => {
    if (activeStep === 0) {
      const isValid = validateStepOne(
        branchBasicInfo,
        branchSettings,
        setErrors,
        t,
        branch.status,
        existingFiles,
        true,
        isDominicanRepublic,
        countryCode
      );
      if (isValid) {
        setActiveStep((prevStep) => prevStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleBranchInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBranchBasicInfo((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBranchSettings((prev) => {
      const newSettings = { ...prev, [e.target.name]: e.target.value };

      if (e.target.name === "personType") {
        newSettings.identityType =
          e.target.value === "PHYSICAL" ? branchSettings.identityType : "CUIT";
      }

      return newSettings;
    });
    if (e.target.name === "personType" || e.target.name === "identityType") {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.identityNumber;
        return newErrors;
      });
    }
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleManagerInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManagerInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleSwitchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "branchConfig" | "manager" | "branchBasicInfo"
  ) => {
    if (type === "branchConfig") {
      setBranchSettings((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else if (type === "manager") {
      setManagerInfo((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setBranchBasicInfo((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const updatedReviewLink = refactorLink(branchSettings.googleReviewLink);
    if (activeStep === 0) {
      const isValid = validateStepOne(
        branchBasicInfo,
        branchSettings,
        setErrors,
        t,
        branch.status,
        existingFiles,
        true,
        isDominicanRepublic,
        countryCode
      );
      if (!isValid) {
        return;
      }
    }

    if (requiresTipAccountFlow && activeStep === 1) {
      const isValid = validateStepTwo(managerInfo, setErrors, t, existingFiles);
      if (!isValid) {
        return;
      }
    }
    setLoading(true);

    const originalEqualSplitTip = branch.equalSplitTip;
    const currentEqualSplitTip = branchSettings.equalSplitTip;

    // Comprimir imágenes si existen
    const compressedAfipFile = branchBasicInfo.afipFile ? await compressImage(branchBasicInfo.afipFile) : null;
    const compressedManagerDniFront = managerInfo.managerDniFront ? await compressImage(managerInfo.managerDniFront) : null;
    const compressedManagerDniBack = managerInfo.managerDniBack ? await compressImage(managerInfo.managerDniBack) : null;
    const compressedManagerSelfie = managerInfo.managerSelfie ? await compressImage(managerInfo.managerSelfie) : null;

    // Solo CLOVER sin tip-account: no Paycloud. Con tip-account completa INCOMPLETE normal.
    const effectiveBranchSettings = isCloverWithoutTipAccount
      ? { ...branchSettings, createCBU: false }
      : branchSettings;

    // Construir parámetros según el país (escalable)
    const params = await buildEditBranchParamsByCountry({
      countryCode,
      branchId: branch.branchId,
      branchBasicInfo,
      branchSettings: effectiveBranchSettings,
      managerInfo: {
        ...managerInfo,
        isInCharge: requiresTipAccountFlow ? false : managerInfo.isInCharge,
      },
      updatedReviewLink,
      branchStatus: isCloverWithoutTipAccount ? BranchStatus.APPROVED : branch.status,
      paycloudEntityId: isCloverWithoutTipAccount ? 0 : branch.paycloudEntityId ?? 0,
      paycloudUserId: isCloverWithoutTipAccount ? 0 : branch.paycloudUserId ?? 0,
      compressedAfipFile,
      compressedManagerDniFront,
      compressedManagerDniBack,
      compressedManagerSelfie,
    });

    await branchService.updateBranch(
      params,
      async (data) => {
        // Actualizar la sucursal con la respuesta del backend
        const updatedBranch: PaycloudBranch = {
          ...branch,
          name: data.name,
          fantasyName: branchBasicInfo.fantasyName,
          address: data.address,
          phone: data.phone,
          active: data.active,
          googleLink: updatedReviewLink,
          ...(isCloverWithoutTipAccount && {
            withTipAccount: false,
            status: BranchStatus.APPROVED,
          }),
          ...(wasIncompleteClover && {
            status: BranchStatus.PENDING,
            tributaryIdentifier: branchSettings.identityNumber,
          }),
          // Actualizar archivos si vienen en la respuesta
          ...(data.files && {
            managerDniFront: data.files.managerDniFront,
            managerDniBack: data.files.managerDniBack,
            managerSelfie: data.files.managerSelfie,
            afipFile: data.files.afipFile,
          })
        };

        updateBranch?.([updatedBranch]);
        
        if (
          originalEqualSplitTip !== currentEqualSplitTip &&
          !currentEqualSplitTip
        ) {
          setLoading(false);
          setIsWarningModalOpen(true);
        } else {
          setLoading(false);
          setIsModalOpen && setIsModalOpen(false);
          showToast(t("branches.edit.success"), "success");
        }

        // CBU Clover: recién después de completar INCOMPLETE (ya hay CUIT)
        if (wasIncompleteClover) {
          onIncompleteCloverCompleted?.(
            updatedBranch,
            branchSettings.identityNumber
          );
        }
      },
      (error) => {
        setLoading(false);
        showToast(error, "error");
      }
    );
  };

  return (
    <>
      <LoadingModal isOpen={loading || isLoadingFiles} />
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        {requiresTipAccountFlow && (
          <Stepper
            activeStep={activeStep}
            className="mb-8"
            sx={{
              "& .MuiStepLabel-label": {
                fontFamily: "Montserrat",
              },
              "& .MuiStepLabel-root .Mui-completed": {
                color: "#EF0BB8",
              },
              "& .MuiStepLabel-root .Mui-active": {
                color: "#EF0BB8",
              },
            }}
          >
            {[t("branches.create.step-1"), t("branches.create.step-2")].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <div className="flex-1">
          {activeStep === 0 ? (
            <div className="grid px-6 gap-4">
              <BranchInfoStep
                branchBasicInfo={branchBasicInfo}
                branchSettings={branchSettings}
                errors={errors}
                handleBranchBasicInfoChange={handleBranchInfoChange}
                handleBranchSettingsChange={handleSettingsChange}
                handleSwitchChange={handleSwitchChange}
                withSplitTip={true}
                disabled={loading}
                branchStatus={branch.status}
                isEditMode={true}
                existingFiles={existingFiles}
              />

              {requiresTipAccountFlow && (
                <div className="w-full flex justify-between items-center">
                  <div className="flex flex-col">
                    <label htmlFor="isInCharge" className="text-black">
                      {t("branches.create.legal-representative-question")}
                    </label>
                    <FormHelperText sx={{ fontFamily: "Montserrat" }}>
                      {t("branches.create.legal-representative-question-note")}
                    </FormHelperText>
                  </div>
                  <Switch
                    id="isInCharge"
                    name="isInCharge"
                    checked={managerInfo.isInCharge || branch.status !== BranchStatus.INCOMPLETE}
                    onChange={(e) => handleSwitchChange(e, "manager")}
                    disabled={loading || branch.status === BranchStatus.INCOMPLETE}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
                        bgcolor: "#EF0BB8",
                      },
                      "& .MuiSwitch-track": {
                        bgcolor: "#898989",
                      },
                      "& .MuiSwitch-thumb": {
                        bgcolor: "#EF0BB8",
                      },
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="px-6">
              <ManagerFields
                disabled={loading}
                errors={errors}
                handleManagerInfoChange={handleManagerInfoChange}
                managerInfo={managerInfo}
                existingFiles={existingFiles}
              />
            </div>
          )}
        </div>

        <div className="flex justify-center mt-8 mx-6">
          {requiresTipAccountFlow ? (
            <div className="flex w-full gap-4">
              <Button
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
                sx={{
                  color: "#EF0BB8",
                  fontFamily: "Montserrat",
                  fontWeight: 300,
                  background: "#fff",
                  border: "1px solid #EF0BB8",
                  borderRadius: "16px",
                  padding: "2px 8px",
                  flex: 1,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    opacity: 0,
                  },
                }}
              >
                {t("branches.create.back")}
              </Button>

              {activeStep === 1 ? (
                <div className="flex-1">
                  <ButtonForm loading={loading}>
                    {t("branches.edit.submit")}
                  </ButtonForm>
                </div>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={loading}
                  sx={{
                    color: "#fff",
                    fontFamily: "Montserrat",
                    fontWeight: 300,
                    background: "#EF0BB8",
                    border: "1px solid #EF0BB8",
                    borderRadius: "16px",
                    padding: "2px 8px",
                    flex: 1,
                    textTransform: "none",
                    "&:hover": {
                      background: "#d109a3",
                    },
                  }}
                >
                  {t("branches.create.next")}
                </Button>
              )}
            </div>
          ) : (
            <ButtonForm loading={loading}>{t("branches.edit.submit")}</ButtonForm>
          )}
        </div>
      </form>

      {isWarningModalOpen && (
        <WarningModal
          open={isWarningModalOpen}
          title={t("warning")}
          message={t("branches.edit.change-split")}
          onClose={() => {
            setIsWarningModalOpen(false);
            setIsModalOpen && setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
}
